export interface SnakeColors {
  head: number
  body: [number, number]
}

export const PLAYER_COLOR_VARIANT_COUNT = 64
const PLAYER_COLOR_KEY_OFFSET = 1_000_000

function mix32(value: number): number {
  let x = value >>> 0
  x ^= x >>> 16
  x = Math.imul(x, 0x7feb352d)
  x ^= x >>> 15
  x = Math.imul(x, 0x846ca68b)
  x ^= x >>> 16
  return x >>> 0
}

function hash01(key: number, salt: number): number {
  return mix32((key ^ Math.imul(salt, 0x9e3779b1)) >>> 0) / 0xFFFFFFFF
}

function circularDistance(a: number, b: number): number {
  const diff = Math.abs(a - b) % 1
  return diff > 0.5 ? 1 - diff : diff
}

function pushHueAway(baseHue: number, candidateHue: number, minimumDistance: number): number {
  if (circularDistance(baseHue, candidateHue) >= minimumDistance) return candidateHue
  return (baseHue + minimumDistance + (candidateHue * 0.37)) % 1
}

function hsvToHex(h: number, s: number, v: number): number {
  const i = Math.floor(h * 6)
  const f = h * 6 - i
  const p = v * (1 - s)
  const q = v * (1 - f * s)
  const t = v * (1 - (1 - f) * s)

  let r = 0
  let g = 0
  let b = 0

  switch (i % 6) {
    case 0: r = v; g = t; b = p; break
    case 1: r = q; g = v; b = p; break
    case 2: r = p; g = v; b = t; break
    case 3: r = p; g = q; b = v; break
    case 4: r = t; g = p; b = v; break
    case 5: r = v; g = p; b = q; break
  }

  return ((Math.round(r * 255) & 0xFF) << 16)
    | ((Math.round(g * 255) & 0xFF) << 8)
    | (Math.round(b * 255) & 0xFF)
}

export function mixColors(colorA: number, colorB: number, ratio: number): number {
  const t = Math.max(0, Math.min(1, ratio))
  const ar = (colorA >> 16) & 0xFF
  const ag = (colorA >> 8) & 0xFF
  const ab = colorA & 0xFF
  const br = (colorB >> 16) & 0xFF
  const bg = (colorB >> 8) & 0xFF
  const bb = colorB & 0xFF

  const r = Math.round(ar + (br - ar) * t)
  const g = Math.round(ag + (bg - ag) * t)
  const b = Math.round(ab + (bb - ab) * t)

  return (r << 16) | (g << 8) | b
}

export function toCssHex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`
}

export function sanitizePlayerColorSeed(seed: number): number {
  if (!Number.isFinite(seed) || seed < 0) return 0
  return Math.floor(seed) % PLAYER_COLOR_VARIANT_COUNT
}

export function getPlayerColorKey(seed: number): number {
  return PLAYER_COLOR_KEY_OFFSET + sanitizePlayerColorSeed(seed)
}

export function generateSnakeColors(colorKey: number): SnakeColors {
  const normalizedKey = (Math.round(colorKey) || 0) >>> 0

  const hueA = hash01(normalizedKey, 11)
  let hueB = hash01(normalizedKey, 29)
  let hueHead = hash01(normalizedKey, 47)

  hueB = pushHueAway(hueA, hueB, 0.16)
  hueHead = pushHueAway(hueA, hueHead, 0.10)
  hueHead = pushHueAway(hueB, hueHead, 0.10)

  const satA = 0.78 + hash01(normalizedKey, 61) * 0.2
  const satB = 0.76 + hash01(normalizedKey, 79) * 0.22
  const satHead = 0.62 + hash01(normalizedKey, 97) * 0.2

  const valA = 0.92 + hash01(normalizedKey, 113) * 0.08
  const valB = 0.9 + hash01(normalizedKey, 131) * 0.1
  const valHead = 0.96 + hash01(normalizedKey, 149) * 0.04

  return {
    head: hsvToHex(hueHead, satHead, valHead),
    body: [
      hsvToHex(hueA, satA, valA),
      hsvToHex(hueB, satB, valB),
    ],
  }
}
